import { useStore } from "@nanostores/react";
import { atom, computed, type ReadableAtom, type WritableAtom } from "nanostores";
import { MEDIA_QUERIES } from "@remi/constants";

export type MediaQueryKey = keyof typeof MEDIA_QUERIES;
export type MediaQueryResults = Record<MediaQueryKey, boolean>;

const SSR_DEFAULTS: MediaQueryResults = {
  maxSm: false,
  maxMd: false,
  maxLg: false,
  maxXl: false,
  isSm: true,
  isMd: true,
  isLg: true,
  isXl: false,
  is2Xl: false,
  minH1000: true,
};

/** @public */
export type MediaStore = WritableAtom<MediaQueryResults>;

let mediaStore: MediaStore | null = null;
const selectedStores = new Map<MediaQueryKey, ReadableAtom<boolean>>();

export function getMediaStore(): MediaStore {
  if (mediaStore) {
    return mediaStore;
  }

  const mqls = Object.entries(MEDIA_QUERIES).map(
    ([key, query]) => [key, typeof window !== "undefined" ? window.matchMedia(query) : null] as const,
  );

  const readAll = (): MediaQueryResults => {
    const result: MediaQueryResults = { ...SSR_DEFAULTS };
    for (const [key, mql] of mqls) {
      if (mql) {
        // biome-ignore lint/plugin/no-type-assertions: key from Object.entries loses type
        result[key as MediaQueryKey] = mql.matches;
      }
    }
    return result;
  };

  mediaStore = atom(readAll());
  const currentStore = mediaStore;

  if (typeof window !== "undefined") {
    let rafId: number | null = null;
    const handler = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        currentStore.set(readAll());
        rafId = null;
      });
    };
    for (const [, mql] of mqls) {
      mql?.addEventListener("change", handler);
    }
  }

  return mediaStore;
}

function getSelectedStore<K extends MediaQueryKey>(key: K) {
  let selected = selectedStores.get(key);
  if (!selected) {
    const store = getMediaStore();
    selected = computed(store, (state) => state[key]);
    selectedStores.set(key, selected);
  }
  return selected;
}

export function useBreakpoint(): MediaQueryResults;
export function useBreakpoint<K extends MediaQueryKey>(key: K): boolean;
export function useBreakpoint<K extends MediaQueryKey>(key?: K): MediaQueryResults | boolean {
  const store = key === undefined ? getMediaStore() : getSelectedStore(key);
  return useStore(store);
}
